import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { t } from "ttag";
import { z } from "zod";

import { CATEGORY_COLOR } from "@/features/products/models";
import { LogoLoader } from "@/shared/components";
import { Badge, Button, Checkbox, Dialog, Form } from "@/shared/ui";
import { useUpdateMedicationStatus } from "../hooks";
import { deliveredMedicationSchema } from "../models";
import type { RefillRequest } from "../models";

const formSchema = z.object({
  medications: z.array(deliveredMedicationSchema),
});

interface DelayedRequestsSelectionProps {
  refillRequests: RefillRequest[];
  onSuccess: () => void;
}

export const DelayedRefillRequestsSelection = ({
  refillRequests,
  onSuccess,
}: DelayedRequestsSelectionProps) => {
  const updateMedicationStatusMutation = useUpdateMedicationStatus();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      medications: refillRequests.map((refillRequest) => ({
        id: refillRequest.id,
        delivered: false,
      })),
    },
  });

  const handleSubmit = form.handleSubmit(
    (values) =>
      void updateMedicationStatusMutation.mutate(values.medications, {
        onSuccess,
      }),
  );

  return (
    <Form.Provider {...form}>
      <form id="delayed-refill-form" onSubmit={handleSubmit}>
        <Form.Field
          defaultValue={[]}
          control={form.control}
          name="medications"
          render={({ field }) => (
            <Form.Item className="gap-2">
              {refillRequests.map((refillRequest) => (
                <Form.Field
                  key={refillRequest.id}
                  control={form.control}
                  name="medications"
                  render={() => {
                    return (
                      <Form.Item key={refillRequest.id}>
                        <Form.Control>
                          <Checkbox.Item
                            isCard
                            className="w-full"
                            checked={field.value?.some(
                              (request) =>
                                request.id === refillRequest.id &&
                                request.delivered,
                            )}
                            onCheckedChange={(checked) => {
                              field.onChange(
                                field.value.map((deliveredMedication) =>
                                  deliveredMedication.id === refillRequest.id
                                    ? {
                                        ...deliveredMedication,
                                        delivered: checked,
                                      }
                                    : deliveredMedication,
                                ),
                              );
                            }}
                          >
                            <Checkbox.Label className="inline-flex justify-between">
                              {refillRequest.product.name}

                              <Badge
                                color={
                                  CATEGORY_COLOR[
                                    refillRequest.product.category.slug
                                  ]
                                }
                                size="small"
                              >
                                {refillRequest.product.category.name}
                              </Badge>
                            </Checkbox.Label>
                          </Checkbox.Item>
                        </Form.Control>
                      </Form.Item>
                    );
                  }}
                />
              ))}

              <Form.Message />
            </Form.Item>
          )}
        />
      </form>

      <Dialog.Footer>
        <Button form="delayed-refill-form" type="submit" size="lg">
          {t`Confirm`}
        </Button>
      </Dialog.Footer>
      <LogoLoader show={updateMedicationStatusMutation.isPending} fullscreen />
    </Form.Provider>
  );
};
